// src/components/map/UserLocationMarker.tsx
//
// The little blue puck representing the user's current location on
// every map surface (MapPage, MiniMap, WalkingTour). Replaces the
// three near-identical UserDot components that previously lived in
// each of those files.
//
// Visuals: a Google-Maps-style pulsing blue dot, with a translucent
// blue cone fanning outward in the direction the user is facing
// when a heading is available. The cone gracefully disappears if no
// compass / GPS heading is detectable so we don't lie about
// orientation.
//
// Render is intentionally a plain DOM/Box subtree — the parent
// <AdvancedMarker> handles positioning at the user's lat/lng. We do
// not call useMap or any maps APIs here; this component is map-SDK
// agnostic.

import React from "react";
import { Box } from "@mui/material";
import { useDeviceHeading } from "../../hooks/useDeviceHeading";

interface Props {
  /** Diameter of the inner blue dot in px. Cone scales with this. */
  size?: number;
  /** Show the heading cone. Default true. Set false to render just
   *  the dot (e.g. for very small thumbnails). */
  showHeading?: boolean;
}

export const UserLocationMarker: React.FC<Props> = ({
  size = 16,
  showHeading = true,
}) => {
  const heading = useDeviceHeading();
  const hasHeading = showHeading && heading != null;

  // Cone radius is fixed in CSS pixels regardless of map zoom — same
  // convention as Google Maps. This keeps the cone readable when
  // zoomed out and avoids covering nearby pins when zoomed in.
  const coneRadius = 56;

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        // Center the dot inside the AdvancedMarker anchor box.
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Allow the cone to extend outside the box.
        overflow: "visible",
      }}
    >
      {/* Heading cone — rendered behind the dot so the dot floats on
          top. The cone is an SVG triangle with a radial-gradient
          fill, rotated to match `heading`. CSS transition smooths
          rapid heading deltas. */}
      {hasHeading && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: coneRadius * 2,
            height: coneRadius * 2,
            // Rotate around the dot's center. -50%/-50% places the
            // SVG so its center is at the marker's anchor point.
            transform: `translate(-50%, -50%) rotate(${heading}deg)`,
            transformOrigin: "center center",
            transition: "transform 200ms linear",
            pointerEvents: "none",
          }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 100 100"
            style={{ display: "block" }}
          >
            <defs>
              <radialGradient
                id="userHeadingCone"
                cx="50%"
                cy="50%"
                r="50%"
                fx="50%"
                fy="50%"
              >
                <stop offset="0%" stopColor="#1a73e8" stopOpacity="0.55" />
                <stop offset="60%" stopColor="#1a73e8" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#1a73e8" stopOpacity="0" />
              </radialGradient>
            </defs>
            {/* Cone vertex at center (50,50), opening upward (toward
                heading 0°). The polygon spans roughly 70° of arc,
                which is where Google Maps lands too — wide enough to
                read at a glance, narrow enough to convey direction. */}
            <polygon
              points="50,50 18,5 82,5"
              fill="url(#userHeadingCone)"
            />
          </svg>
        </Box>
      )}

      {/* The dot itself. */}
      <Box
        sx={{
          position: "relative",
          width: size,
          height: size,
          borderRadius: "50%",
          bgcolor: "#1a73e8",
          border: "3px solid white",
          boxShadow:
            "0 0 0 1px rgba(0,0,0,0.15), 0 2px 6px rgba(26,115,232,0.5)",
          // Pulse halo
          "&::before": {
            content: '""',
            position: "absolute",
            inset: -8,
            borderRadius: "50%",
            bgcolor: "rgba(26,115,232,0.25)",
            animation: "userLocPulse 2s ease-out infinite",
          },
          "@keyframes userLocPulse": {
            "0%": { transform: "scale(0.7)", opacity: 0.8 },
            "100%": { transform: "scale(1.6)", opacity: 0 },
          },
        }}
      />
    </Box>
  );
};
